/** biome-ignore-all lint/style/noNonNullAssertion: <because I'm too tried to fix this> */
import { NotificationTypes } from '@awell/ui-kit/components/NotificationMessage'
import { t } from 'i18next'
import { isNil } from 'lodash'
import { useEffect, useState } from 'react'
import type {
  DynamicFormActivityInputs,
  DynamicFormActivityOutputs,
  FormActivityInputs,
  FormActivityOutputs
} from '../../generated/types-orchestration'
import { useActivity } from '../useActivityFeed'
import { useNotifications } from '../useNotifications'
import type { Answer, AnswerInput } from './types'
import { useGetFormResponseQuery, useSubmitFormResponseMutation } from './types'

interface UseFormActivityHook {
  loading: boolean
  disabled: boolean
  onSubmit: (response: Array<AnswerInput>) => Promise<boolean>
  answers: Array<Answer>
}

export const useFormActivity = ({
  activityId,
  careflowId
}: {
  activityId: string
  careflowId: string
}): UseFormActivityHook => {
  const { notify, notifyError } = useNotifications()
  const [submitFormResponse] = useSubmitFormResponseMutation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [answers, setAnswers] = useState<Array<Answer>>([])
  const {
    activity,
    loading: activityLoading,
    error: activityError
  } = useActivity({
    activityId
  })

  const {
    data: responseData,
    loading: responseLoading,
    error: responseError
  } = useGetFormResponseQuery({
    variables: {
      pathway_id: careflowId,
      activity_id: activityId
    }
  })

  useEffect(() => {
    let formattedAnswers: Array<Answer> = []
    if (activity && activity.inputs && activity.outputs) {
      const { inputs, outputs } = activity
      const outputAnswers = (outputs as DynamicFormActivityOutputs | FormActivityOutputs)?.answers ?? []
      const questions =
        (inputs as FormActivityInputs)?.form?.questions ??
        (inputs as DynamicFormActivityInputs)?.dynamicForm?.questions ??
        []
      formattedAnswers = questions
        .map(question => {
          return {
            question_id: question.id,
            value: outputAnswers.find(a => a.question_id === question.id)?.value ?? null,
            value_type: question.dataPointValueType
          }
        })
        .filter(q => !isNil(q.value)) as Array<Answer>
    }
    if (responseData && formattedAnswers.length === 0) {
      formattedAnswers = responseData.formResponse.response.answers
    }
    setAnswers(formattedAnswers)
  }, [responseData, activity])

  if (responseError || activityError) {
    notifyError({
      error: responseError || activityError,
      message: t('get_form_response_error_notification')
    })
  }

  const onSubmit = async (response: Array<AnswerInput>): Promise<boolean> => {
    setIsSubmitting(true)
    try {
      await submitFormResponse({
        variables: {
          input: {
            activity_id: activity?.id!,
            response
          }
        },
        refetchQueries: ['GetFormResponse'],
        awaitRefetchQueries: true
      })
      notify({
        message: t('form:success_notification'),
        notifyType: NotificationTypes.success
      })
      return true
    } catch (error) {
      setIsSubmitting(false)
      notifyError({
        error,
        message: t('form:submit_error_notification')
      })
      return false
    }
  }

  return {
    loading: responseLoading || activityLoading,
    onSubmit,
    answers,
    disabled: isSubmitting
  }
}
